import { useContext, useState, useRef } from "react"
import { FirebaseContext } from "../../firebase"
import { Link } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"

import _ from "lodash"
import { Calendar } from "primereact/calendar"

import { useQuery } from "@apollo/client"
import { SplitButton } from "primereact/splitbutton"
// queries
import { useNavigate } from "react-router-dom"
import { DOCUMENTS_PARTICIPANT } from "./queries"

// Helpers
import { navigateWithOrigin } from "../../helpers/navigation"
import { callCloudFunction } from "../../helpers/cloudFunctionRequests"

// Context
import { UserContext } from "../../context/userContext"
import { parseDate } from "../../helpers/dateNormalization"
// Prime React
import { Toast } from "primereact/toast"

import LoaderFullPage, { LoaderSmall } from "../../components/Loaders"

import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"

import styles from "../../styles/Page-Engagements.module.css"

import GLOBALS_STATUS from "../../config/globalsStatus"

import {
  formatDate,
  handleOptsForFilter,
} from "../../components/DataTable/helpers"

import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import { LogError } from "../../helpers/logger"
import { Dialog } from "primereact/dialog"
import ErrorMessage from "../Participant/ParticipantProfile/components/ErrorMessage"

export default function ParticipantDocumentManagement() {
  const [data, setData] = useState(null)
  const [error, setError] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [dateRange, setDateRange] = useState(null)

  const [unassignedKioskMember, setUnassignedKioskMember] = useState(false)
  const [hasActiveServiceEpisode, setHasActiveServiceEpisode] = useState(false)

  const [formId] = useState(() => uuidv4())

  const user = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const navigate = useNavigate()

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const { getStorage, ref, getDownloadURL } = firebase.storage
  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`
  const ID_TEMPLATE = "document_record_participant"
  const PID = user.current_participant_id || "not-set"

  const toast = useRef(null)

  const getDownloadURLFromStorage = async (path) => {
    try {
      if (_.isEmpty(path)) return null
      const storage = getStorage()
      const fileRef = ref(storage, path)
      return await getDownloadURL(fileRef)
    } catch (error) {
      return null
    }
  }

  const handleDelete = async (idStamp) => {
    if (submitting) {
      return false
    }

    const currentPid = PID
    const deleteFormId = `${formId}_idStamp_${idStamp}`

    try {
      setSubmitting(true)
      const postBody = {
        idStamp,
        idTemplate: ID_TEMPLATE,
        organization_alias: user.organization_alias,
        organization_id: user.organization_id,
        enterprise_id: user.enterprise_id,
        enterprise_alias: user.enterprise_alias,
        participant_alias: user.participant_alias || "not-set",
        participant_membership_id: user.participant_membership_id || "not-set",
        participant_id: currentPid,
        staff_alias: user.staff_alias,
        staff_id: user.staff_id,
        form_id: deleteFormId,
        values: {
          document__document_record__participant: {
            document_status: "deleted",
          },
        },
      }

      // Note: Date normalization is not needed here. The payload does not contain any dates.
      await callCloudFunction("blockEdit", postBody, user.tokens.getMainToken())

      try {
        await callCloudFunction(
          "deleteStagedFormSubmission",
          { form_id: deleteFormId },
          user.tokens.getMainToken(),
        )
      } catch (cleanupError) {
        LogError("Failed to clean up staged submission:", cleanupError)
      }

      toast.current?.show({
        severity: "success",
        summary: "Deleted",
        detail: "Document deleted successfully",
      })
      return true
    } catch (error) {
      LogError("Error deleting document record:", error)
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to delete document",
      })
      setError({ message: error.message, formId: deleteFormId })
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const { loading, refetch } = useQuery(DOCUMENTS_PARTICIPANT, {
    variables: {
      idTemplate: ID_TEMPLATE,
      organization_id: user.organization_id,
      participant_id: user.participant_id,
      staff_id: user.staff_id,
      participant_membership_id: user.participant_membership_id,
    },
    fetchPolicy: "no-cache",
    skip:
      !user.staff_id || !user.participant_id || !user.participant_membership_id,
    onCompleted: async (data) => {
      setUnassignedKioskMember(
        data.participant.staff_full_name === "Unassigned Kiosk Member",
      )
      setHasActiveServiceEpisode(!!data.serviceEpisode)

      if (_.isEmpty(data.documentRecords)) {
        return setData(null)
      }

      const dataFormatted = JSON.parse(data.documentRecords.data)

      const handleData = async () =>
        Promise.all(
          _.map(dataFormatted, async (blocks) => {
            const formBlock = _.find(blocks, { block_name: "form" })
            const documentBlock = _.find(blocks, {
              block_name: "document__document_record__participant",
            })
            const downloadUrl = await getDownloadURLFromStorage(
              documentBlock?.values?.document_participant_file,
            )

            let actionButton
            _.includes(
              GLOBALS_STATUS.ACTIVE_STATUS,
              user.participant_membership_status,
            )
              ? (actionButton = (
                  <SplitButton
                    label="View"
                    disabled={submitting}
                    onClick={() => {
                      if (!downloadUrl) {
                        return toast.current.show({
                          severity: "error",
                          summary: "The reference to the File was not found.",
                          detail:
                            "Please reupload the a File by clicking Edit.",
                        })
                      }
                      const getFilename = () => {
                        const splitPath = _.split(
                          documentBlock?.values?.document_participant_file,
                          "/",
                        )
                        return splitPath[splitPath.length - 1]
                      }
                      const filename = getFilename()
                      const xhr = new XMLHttpRequest()
                      xhr.responseType = "blob"
                      xhr.onload = () => {
                        const blob = xhr.response
                        const link = document.createElement("a")
                        link.href = URL.createObjectURL(blob)
                        link.download = filename
                        link.click()
                      }
                      xhr.open("GET", downloadUrl)
                      xhr.send()
                    }}
                    model={[
                      {
                        label: "Edit",
                        command: () => {
                          navigate(
                            `/form/${formBlock.idTemplate}/1/${formBlock.idStamp}`,
                          )
                        },
                      },
                      {
                        label: "View Document Upload Session",
                        command: async () => {
                          await user.changeParticipant(formBlock.participant_id)
                          navigateWithOrigin(
                            navigate,
                            `/form/view/${formBlock.idTemplate}/${formBlock.idStamp}`,
                          )
                        },
                      },
                      {
                        label: "Delete",
                        command: async () => {
                          const deleted = await handleDelete(formBlock.idStamp)
                          if (deleted) {
                            refetch()
                          }
                        },
                      },
                    ]}
                  />
                ))
              : (actionButton = (
                  <SplitButton
                    label="View"
                    disabled={submitting}
                    onClick={() => {
                      if (!downloadUrl) {
                        return toast.current.show({
                          severity: "error",
                          summary: "The reference to the File was not found.",
                          detail:
                            "Please reupload the a File by clicking Edit.",
                        })
                      }
                      const getFilename = () => {
                        const splitPath = _.split(
                          documentBlock?.values?.document_participant_file,
                          "/",
                        )
                        return splitPath[splitPath.length - 1]
                      }
                      const filename = getFilename()
                      const xhr = new XMLHttpRequest()
                      xhr.responseType = "blob"
                      xhr.onload = () => {
                        const blob = xhr.response
                        const link = document.createElement("a")
                        link.href = URL.createObjectURL(blob)
                        link.download = filename
                        link.click()
                      }
                      xhr.open("GET", downloadUrl)
                      xhr.send()
                    }}
                    model={[
                      {
                        label: "View Document Upload Session",
                        command: async () => {
                          await user.changeParticipant(formBlock.participant_id)
                          navigateWithOrigin(
                            navigate,
                            `/form/view/${formBlock.idTemplate}/${formBlock.idStamp}`,
                          )
                        },
                      },
                    ]}
                  />
                ))

            const handleLocations = (row) => {
              const locationsList = _.map(row, ({ label }) => label)
              return _.join(locationsList, ", ")
            }

            return {
              date: parseDate(formBlock.values.form_date),
              locations: handleLocations(formBlock.values.form__location_ids),
              document_name: documentBlock?.values?.document_participant_name,
              document_type: documentBlock?.values?.document_participant_type,
              facilitating_staff: formBlock.values.staff_full_name || "",
              edit: actionButton,
            }
          }),
        )
      const items = await handleData()
      setData(items)
    },
  })

  if (loading) return <LoaderFullPage />
  if (!user.current_participant_id) {
    return <div>No Participant selected.....</div>
  }
  const uniqueLocations = handleOptsForFilter(data, "locations")
  const uniqueFacilitatingStaff = handleOptsForFilter(
    data,
    "facilitating_staff",
  )
  const uniqueDocumentType = handleOptsForFilter(data, "document_type")

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Locations",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      enableFilter: true,
    },
    {
      label: "Facilitating Staff",
      value: filters.facilitating_staff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("facilitating_staff", e.value),
      onClear: () => onFilterChange("facilitating_staff", null),
      enableFilter: true,
    },
    {
      label: "Document Type",
      value: filters.document_type?.value || "",
      options: uniqueDocumentType,
      onChange: (e) => onFilterChange("document_type", e.value),
      onClear: () => onFilterChange("document_type", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "document_name", header: "Document Name", sortable: true },
    { field: "document_type", header: "Document Type", sortable: true },
    {
      field: "facilitating_staff",
      header: "Facilitating Staff",
      sortable: true,
    },
    { field: "edit", header: "Action" },
  ]

  const isKioskMember =
    user.participant_membership_status === GLOBALS_STATUS.KIOSK_MEMBER &&
    unassignedKioskMember

  const isActiveStatus = _.includes(
    GLOBALS_STATUS.ACTIVE_STATUS,
    user.participant_membership_status,
  )

  const shouldShowAddDoc =
    isActiveStatus || (isKioskMember && hasActiveServiceEpisode)

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <ParticipantSidebar />
      </div>
      <div className={`main-content ${styles.loaderOverlayHost}`}>
        <ParticipantProfileBoxSmall />
        {error && (
          <Dialog header="Error" visible={true} onHide={() => setError(null)}>
            <ErrorMessage
              formId={error?.formId || formId}
              message={error?.message || "An unexpected error occurred."}
              onCancel={() => {
                setError(null)
              }}
            />
          </Dialog>
        )}
        <div>
          <div>
            <h1 className={styles.title}>Documents Management</h1>
            {shouldShowAddDoc && (
              <Link to="/form/document_record_participant/1" className="btn">
                Add New Documents
              </Link>
            )}
            {submitting && (
              <div className={styles.deleteLoaderOverlay}>
                <LoaderSmall />
                <span>Deleting document...</span>
              </div>
            )}
            <br />
            <br />
            <Toast ref={toast} position="top-right" />
            <GenericTable
              data={data}
              columns={columns}
              filters={filters}
              header={
                <FilterHeader
                  filters={filterConfig}
                  globalFilterValue={globalFilterValue}
                  onGlobalFilterChange={onGlobalFilterChange}
                  onFilterChange={onFilterChange}
                  clearGlobalFilter={clearGlobalFilter}
                  clearAllFilters={handleClearAllFilters}
                />
              }
              paginator
              rows={50}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
